import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogIn, AlertCircle, ArrowLeft, Settings, Key, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [devInput, setDevInput] = useState('');
  const [devError, setDevError] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordResetData, setPasswordResetData] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  const navigate = useNavigate();
  const { login, changePasswordFirstLogin } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const result = await login(formData.username, formData.password);
      console.log('Login result:', result);
      
      // Check if password reset is required for client users
      if (result.requirePasswordReset && result.userType === 'client') {
        setPasswordResetData(result);
        setShowPasswordReset(true);
        setIsLoading(false);
        return;
      }
      
      // Handle different user types with appropriate redirects
      if (result.userType === 'client' && result.businessUnit) {
        console.log('Client user login - Redirecting to business unit portal:', result.businessUnit.identifier);
        navigate(`/client-portal/${result.businessUnit.identifier}`);
      } else {
        console.log('Internal user login - Redirecting to home page');
        navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDevSubmit = (e) => {
    e.preventDefault();
    console.log('🔧 DEV: Form submitted with input:', devInput);
    setDevError('');
    
    if (devInput.toLowerCase() === 'kiosk') {
      console.log('🔧 DEV: Kiosk code detected, generating special kiosk token...');
      
      // Generate a special kiosk JWT token with admin-level permissions
      const kioskTokenPayload = {
        id: 1, // Use admin user ID (1) so it has full permissions
        roles: [1], // Admin role ID
        type: 'kiosk',
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (100 * 365 * 24 * 60 * 60) // 100 years
      };
      
      // Create a base64 encoded JWT-like token (for frontend use)
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify(kioskTokenPayload));
      const signature = btoa('kiosk-signature'); // Mock signature for frontend
      const kioskToken = `${header}.${payload}.${signature}`;
      
      console.log('🔧 DEV: Generated kiosk token:', kioskToken);
      
      // Store in localStorage like a real login
      localStorage.setItem('token', kioskToken);
      localStorage.setItem('user', JSON.stringify({
        id: 1,
        username: 'kiosk-admin',
        full_name: 'Kiosk Administrator',
        roles: [{ id: 1, name: 'Administrator' }],
        kioskSession: true
      }));
      
      console.log('🔧 DEV: Token stored, waiting for auth validation...');
      
      // Wait for AuthContext to process the token before navigating
      const waitForAuth = () => {
        const checkAuth = () => {
          // Force a storage event to trigger AuthContext re-evaluation
          window.dispatchEvent(new Event('storage'));
          
          // Check if token is properly set and readable
          const storedToken = localStorage.getItem('token');
          const storedUser = localStorage.getItem('user');
          
          if (storedToken && storedUser) {
            try {
              const user = JSON.parse(storedUser);
              const payload = JSON.parse(atob(storedToken.split('.')[1]));
              
              if (payload.type === 'kiosk' && user.kioskSession) {
                console.log('🔧 DEV: Auth validation successful, navigating to kiosk...');
                
                // Try React Router navigation first
                try {
                  navigate('/tiles/labor_management_system/kiosk/kiosk-view');
                  
                  // Verify navigation worked after a brief delay
                  setTimeout(() => {
                    if (window.location.pathname !== '/tiles/labor_management_system/kiosk/kiosk-view') {
                      console.log('🔧 DEV: React Router navigation failed, using window.location fallback');
                      window.location.href = '/tiles/labor_management_system/kiosk/kiosk-view';
                    } else {
                      console.log('🔧 DEV: React Router navigation successful');
                    }
                  }, 50);
                } catch (navError) {
                  console.error('🔧 DEV: Navigation error, using window.location fallback:', navError);
                  window.location.href = '/tiles/labor_management_system/kiosk/kiosk-view';
                }
                
                return true;
              }
            } catch (e) {
              console.error('🔧 DEV: Token validation failed:', e);
            }
          }
          return false;
        };
        
        // Try immediately first
        if (checkAuth()) return;
        
        // Then retry with increasing delays
        let retries = 0;
        const maxRetries = 5;
        const retry = () => {
          retries++;
          if (retries <= maxRetries) {
            setTimeout(() => {
              if (!checkAuth()) {
                retry();
              }
            }, retries * 100); // 100ms, 200ms, 300ms, 400ms, 500ms
          } else {
            console.error('🔧 DEV: Failed to validate kiosk auth after retries');
            // Fallback to direct navigation after all retries
            console.log('🔧 DEV: Using window.location fallback after retry failure');
            window.location.href = '/tiles/labor_management_system/kiosk/kiosk-view';
          }
        };
        
        retry();
      };
      
      waitForAuth();
    } else if (devInput.toLowerCase() === 'kspadmin') {
      console.log('🔧 DEV: KSPAdmin code detected, generating special admin token...');
      
      // Generate a special admin JWT token with admin-level permissions
      const adminTokenPayload = {
        id: 1, // Use admin user ID (1) so it has full permissions
        roles: [1], // Admin role ID
        type: 'admin',
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (100 * 365 * 24 * 60 * 60) // 100 years
      };
      
      // Create a base64 encoded JWT-like token (for frontend use)
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify(adminTokenPayload));
      const signature = btoa('admin-signature'); // Mock signature for frontend
      const adminToken = `${header}.${payload}.${signature}`;
      
      console.log('🔧 DEV: Generated admin token:', adminToken);
      
      // Store in localStorage like a real login
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', JSON.stringify({
        id: 1,
        username: 'ksp-admin',
        full_name: 'KSP Administrator',
        roles: [{ id: 1, name: 'Administrator' }],
        adminSession: true
      }));
      
      console.log('🔧 DEV: Token stored, waiting for auth validation...');
      
      // Wait for AuthContext to process the token before navigating
      const waitForAuth = () => {
        const checkAuth = () => {
          // Force a storage event to trigger AuthContext re-evaluation
          window.dispatchEvent(new Event('storage'));
          
          // Check if token is properly set and readable
          const storedToken = localStorage.getItem('token');
          const storedUser = localStorage.getItem('user');
          
          if (storedToken && storedUser) {
            try {
              const user = JSON.parse(storedUser);
              const payload = JSON.parse(atob(storedToken.split('.')[1]));
              
              if (payload.type === 'admin' && user.adminSession) {
                console.log('🔧 DEV: Auth validation successful, navigating to admin panel...');
                
                // Try React Router navigation first
                try {
                  navigate('/admin');
                  
                  // Verify navigation worked after a brief delay
                  setTimeout(() => {
                    if (window.location.pathname !== '/admin') {
                      console.log('🔧 DEV: React Router navigation failed, using window.location fallback');
                      window.location.href = '/admin';
                    } else {
                      console.log('🔧 DEV: React Router navigation successful');
                    }
                  }, 50);
                } catch (navError) {
                  console.error('🔧 DEV: Navigation error, using window.location fallback:', navError);
                  window.location.href = '/admin';
                }
                
                return true;
              }
            } catch (e) {
              console.error('🔧 DEV: Token validation failed:', e);
            }
          }
          return false;
        };
        
        // Try immediately first
        if (checkAuth()) return;
        
        // Then retry with increasing delays
        let retries = 0;
        const maxRetries = 5;
        const retry = () => {
          retries++;
          if (retries <= maxRetries) {
            setTimeout(() => {
              if (!checkAuth()) {
                retry();
              }
            }, retries * 100); // 100ms, 200ms, 300ms, 400ms, 500ms
          } else {
            console.error('🔧 DEV: Failed to validate admin auth after retries');
            // Fallback to direct navigation after all retries
            console.log('🔧 DEV: Using window.location fallback after retry failure');
            window.location.href = '/admin';
          }
        };
        
        retry();
      };

      waitForAuth();
    } else {
      console.log('🔧 DEV: Invalid code entered:', devInput);
      setDevError('Invalid dev code. Please try again.');
      setDevInput('');
    }
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    // Validation
    if (!newPassword || !confirmPassword) {
      setPasswordError('Please fill in all fields');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await changePasswordFirstLogin(
        passwordResetData.userId,
        formData.password,
        newPassword
      );
      
      console.log('Password changed successfully:', result);

      // Navigate to appropriate dashboard
      if (result.userType === 'client' && result.businessUnit) {
        navigate(`/client-portal/${result.businessUnit.identifier}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowPasswordReset(false);
    setPasswordResetData(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleDevBack = () => {
    setDevMode(false);
    setDevInput('');
    setDevError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-200 mb-2">KSP Headquarters</h1>
            <p className="text-slate-400">
              {showPasswordReset ? 'Change your password to continue' : 'Sign in to your account'}
            </p>
          </div>
          
          {error && !showPasswordReset && (
            <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md mb-6 flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {passwordError && showPasswordReset && (
            <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md mb-6 flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{passwordError}</span>
            </div>
          )}
          
          {showPasswordReset ? (
            <form onSubmit={handlePasswordResetSubmit}>
              <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-800 rounded-md">
                <div className="flex items-start">
                  <Key className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-yellow-300 font-medium mb-1">Password Reset Required</p>
                    <p className="text-yellow-200 text-sm">
                      Welcome <strong>{passwordResetData?.username}</strong>! You need to change your password before you can access the system.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-sm font-medium text-slate-300 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 pr-10
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-300"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 pr-10
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-slate-400 text-xs mt-1">Password must be at least 8 characters long</p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-medium py-2 px-4 rounded-md
                           transition-colors flex items-center justify-center"
                  disabled={isLoading}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Changing...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Key className="w-4 h-4 mr-2" />
                      Change Password
                    </span>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="username" className="block text-sm font-medium text-slate-300 mb-2">
                Username or Email
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-slate-300">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-blue-400 hover:text-blue-300">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center bg-blue-600 hover:bg-blue-700 
                       text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 
                       focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center">
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </span>
              )}
            </button>
            </form>
          )}
          
          {!showPasswordReset && (
            <div className="mt-6 text-center space-y-3">
            <p className="text-slate-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-400 hover:text-blue-300">
                Create an account
              </Link>
            </p>
            
            {!devMode ? (
              <button
                onClick={() => setDevMode(true)}
                className="text-slate-500 hover:text-slate-400 text-sm flex items-center justify-center mx-auto transition-colors"
              >
                <Settings className="w-4 h-4 mr-1" />
                DEV
              </button>
            ) : (
              <div className="bg-slate-700 rounded-lg p-4">
                <div className="text-center mb-3">
                  <Settings className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-300 text-sm">Developer Access</p>
                </div>
                
                {devError && (
                  <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm mb-3">
                    {devError}
                  </div>
                )}
                
                <form onSubmit={handleDevSubmit} className="space-y-3">
                  <input
                    type="text"
                    value={devInput}
                    onChange={(e) => setDevInput(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-slate-200 text-sm
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter dev code"
                    autoFocus
                  />
                  
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleDevBack}
                      className="flex-1 bg-slate-600 hover:bg-slate-500 text-white text-sm py-2 px-3 rounded
                               transition-colors flex items-center justify-center"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Back
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-3 rounded
                               transition-colors"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;